// Page context ONLY — never imported from src/sw/ (the SW bundle stays
// analytics-free, same rule as api/client; see web-sw-boundary-rules). Gated by
// the user's analyticsOptOut, checked before SDK construction. Event-spec §3.4.

import {
  bumpCounter,
  captureOnce,
  trackClipboardManagerOpened as trackClipboardManagerOpenedCore,
  trackClipComposerSent as trackClipComposerSentCore,
  trackClipCopiedFromHistory as trackClipCopiedFromHistoryCore,
  trackGlanceDownloadConsented as trackGlanceDownloadConsentedCore,
  trackGlanceViewed as trackGlanceViewedCore,
  trackPairingCodeDisplayed as trackPairingCodeDisplayedCore,
  trackPairingCompleted as trackPairingCompletedCore,
  trackSafetyNumberViewed as trackSafetyNumberViewedCore,
  trackSeenBatch as trackSeenBatchCore,
} from "@reflecto/client-core";
import { createAnalytics, type ReflectoAnalytics } from "@reflecto/client-core/analytics";
import { webStorage } from "../adapters/storage.web";

let instance: ReflectoAnalytics | null = null;

export function getAnalytics(): ReflectoAnalytics {
  if (!instance) {
    instance = createAnalytics({
      surface: "web",
      appVersion: __APP_VERSION__,
      getOptedOut: async () => (await webStorage.get("analyticsOptOut")).analyticsOptOut === true,
      getDistinctId: async () => (await webStorage.get("deviceId")).deviceId,
      getInternalTester: async () =>
        (await webStorage.get("internalTester")).internalTester === true,
    });
  }
  return instance;
}

// Named trackers — the only analytics surface UI code should touch. Fail open, never throw.
//
// Slice 11i: the bodies live in `@reflecto/client-core`'s `analytics/trackers.ts`, shared with
// `apps/extension/src/shared/analytics.ts`. What stays here is what is genuinely this
// surface's: `getAnalytics` above, the three trackers below that only web has, and the binding
// of `webStorage` into each call.

export function trackPairingCodeDisplayed(): void {
  trackPairingCodeDisplayedCore(webStorage, getAnalytics());
}

/**
 * `intent` mirrors the pairing code's onboarding intent (registry-declared on all surfaces;
 * Android sends it since the two-flow PR2) — values are only ever the wire enum, never
 * user-derived. The default is kept HERE rather than in the shared core: the extension passes
 * no argument at all and must keep emitting no `intent` key, so a default inside the core
 * would change its payload.
 */
export async function trackPairingCompleted(
  intent: "mirror" | "assistant" = "mirror"
): Promise<void> {
  return trackPairingCompletedCore(webStorage, getAnalytics(), intent);
}

export function trackGlanceViewed(): void {
  trackGlanceViewedCore(webStorage, getAnalytics());
}

export function trackGlanceDownloadConsented(): void {
  trackGlanceDownloadConsentedCore(webStorage, getAnalytics());
}

/**
 * This surface's pairing was destroyed without the user asking for it. WEB-ONLY: the
 * extension's service worker reports the same event by queueing it instead, so there is no
 * shared body to move.
 *
 * Captured directly rather than queued via `bumpCounter`/`queueMilestone`. That asymmetry is
 * forced: `clearLocalState`'s `clearExcept` allowlist in services/disconnect.ts does not
 * include `analyticsState`, so a queued event would be wiped by the very teardown it reports.
 * The page has a live SDK, so a direct capture has no such problem.
 *
 * Content-free: `trigger` names which involuntary path ran, nothing else.
 */
export function trackPairingLost(trigger: "handshake_refused" | "server_disconnect"): void {
  getAnalytics().capture("pairing_lost", { trigger });
}

export function trackSafetyNumberViewed(): void {
  trackSafetyNumberViewedCore(getAnalytics());
}

export function trackClipboardManagerOpened(): void {
  trackClipboardManagerOpenedCore(getAnalytics());
}

export function trackClipCopiedFromHistory(direction: "sent" | "received"): void {
  trackClipCopiedFromHistoryCore(getAnalytics(), direction);
}

export function trackClipComposerSent(): void {
  trackClipComposerSentCore(getAnalytics());
}

export function trackSeenBatch(items: Array<{ appPackage: string }>): Promise<void> {
  return trackSeenBatchCore(webStorage, getAnalytics(), items);
}

// Dismiss/reply choke points in services/messages.ts — intent-only (phone-side dismissals ride
// SSE, never these helpers). WEB-ONLY: the extension counts these in its service worker.
export function trackDismissIntent(count: number): void {
  if (count <= 0) return;
  void bumpCounter(webStorage, "dismissals_count", count);
  void captureOnce(webStorage, getAnalytics(), "first_dismiss");
}

export function trackReplySent(): void {
  void bumpCounter(webStorage, "replies_sent_count");
  void captureOnce(webStorage, getAnalytics(), "first_reply_sent");
}

// Re-exported, not wrapped. Every other tracker here exists to bind this surface's storage
// adapter; this one binds nothing, so a local function would be a passthrough that only looks
// like the others. The re-export keeps the import path call sites already use, and the shared
// generic still resolves `PaletteCommand` to this host's own type.
export { withCommandAnalytics } from "@reflecto/client-core";
